import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { C, S, BASE } from '../constants';

function PositionChart({ data, anchorRect }) {
  if (!data || data.length < 2) return null;
  const W = 260;
  const PAD = { top: 20, right: 16, bottom: 28, left: 36 };

  // Position section
  const posH = 130;
  const posCH = posH - PAD.top - PAD.bottom;
  const cW = W - PAD.left - PAD.right;

  const vals = data.map(d => d.pos ?? 181);
  const rawMin = Math.min(...vals), rawMax = Math.max(...vals);
  const pad = Math.max(Math.round((rawMax - rawMin) * 0.2), 2);
  const minV = Math.max(1, rawMin - pad), maxV = rawMax + pad;
  const range = maxV - minV || 1;

  const px = i => PAD.left + (i / (vals.length - 1)) * cW;
  const pyPos = v => PAD.top + ((v - minV) / range) * posCH;
  const pts = vals.map((v, i) => `${px(i)},${pyPos(v)}`).join(" ");
  const first = vals[0], last = vals[vals.length - 1];
  const lineColor = last < first ? "#22c55e" : last > first ? "#f87171" : "#94a3b8";

  const tickCount = 3;
  const yTicks = Array.from({ length: tickCount + 1 }, (_, i) =>
    Math.round(minV + (i / tickCount) * (maxV - minV))
  );

  // Price section
  const prices = data.map(d => d.price).filter(v => v != null && v > 0);
  const showPrice = prices.length >= 2;
  const priceH = showPrice ? 80 : 0;
  const pricePAD = { top: 12, right: 16, bottom: 20, left: 52 };
  const priceCH = priceH - pricePAD.top - pricePAD.bottom;
  const priceCW = W - pricePAD.left - pricePAD.right;
  let priceMin = 0, priceMax = 0, priceRange = 1;
  if (showPrice) {
    priceMin = Math.min(...prices);
    priceMax = Math.max(...prices);
    const ppad = Math.max((priceMax - priceMin) * 0.15, priceMax * 0.01);
    priceMin = Math.max(0, priceMin - ppad);
    priceMax = priceMax + ppad;
    priceRange = priceMax - priceMin || 1;
  }
  const pyPrice = v => pricePAD.top + ((priceMax - v) / priceRange) * priceCH;
  const pxPrice = i => pricePAD.left + (i / (data.length - 1)) * priceCW;
  const pricePts = data.map((d, i) => d.price != null ? `${pxPrice(i)},${pyPrice(d.price)}` : null).filter(Boolean);

  const totalH = posH + priceH;

  let top, left;
  if (anchorRect) {
    const popH = totalH + 48;
    left = Math.min(anchorRect.left - 10, window.innerWidth - W - 8);
    left = Math.max(8, left);
    top = anchorRect.top - popH - 6 > 0 ? anchorRect.top - popH - 6 : anchorRect.bottom + 6;
  }

  return createPortal(
    <div style={{
      position: "fixed", top, left, width: W,
      background: "#0f172a", border: "1px solid #1e293b",
      borderRadius: 10, boxShadow: "0 12px 32px rgba(0,0,0,0.7)",
      zIndex: 9999, pointerEvents: "none",
    }}>
      {/* Header */}
      <div style={{ padding: "8px 12px 4px", borderBottom: "1px solid #1e293b", display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8" }}>ПОЗИЦІЯ КОНКУРЕНТА</span>
        <span style={{ fontSize: 10, color: lineColor, fontWeight: 700 }}>
          #{last >= 181 ? "?" : last}
          {first !== last && <span style={{ color: "#64748b" }}> (було #{first >= 181 ? "?" : first})</span>}
        </span>
      </div>

      {/* Position chart */}
      <svg width={W} height={posH}>
        {yTicks.map(tick => {
          const yy = pyPos(tick);
          return (
            <g key={tick}>
              <line x1={PAD.left} x2={W - PAD.right} y1={yy} y2={yy} stroke="#1e293b" strokeWidth={1} strokeDasharray="4,4" />
              <text x={PAD.left - 6} y={yy + 4} textAnchor="end" fontSize={9} fill="#475569">{tick}</text>
            </g>
          );
        })}
        <text x={PAD.left} y={PAD.top - 6} fontSize={8} fill="#475569">↑ краще</text>
        <polyline points={pts} fill="none" stroke={lineColor} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {vals.map((v, i) => {
          const cx = px(i), cy = pyPos(v);
          const isLast = i === vals.length - 1;
          const showLabel = i === 0 || isLast || vals.length <= 8;
          return (
            <g key={i}>
              <circle cx={cx} cy={cy} r={isLast ? 4 : 2.5}
                fill={isLast ? lineColor : "#0f172a"} stroke={lineColor} strokeWidth={1.5} />
              {showLabel && (
                <text x={cx} y={cy - 7} textAnchor="middle" fontSize={9}
                  fill={isLast ? lineColor : "#94a3b8"} fontWeight={isLast ? 700 : 400}>
                  {v >= 181 ? "?" : v}
                </text>
              )}
            </g>
          );
        })}
        {data.map((d, i) => {
          const total = data.length;
          const show = i === 0 || i === total - 1 || total <= 7 ||
            (total > 7 && (i === Math.floor(total / 3) || i === Math.floor(2 * total / 3)));
          if (!show) return null;
          return <text key={i} x={px(i)} y={posH - 6} textAnchor="middle" fontSize={8} fill="#475569">{d.date}</text>;
        })}
      </svg>

      {/* Price chart */}
      {showPrice && (
        <>
          <div style={{ borderTop: "1px solid #1e293b", padding: "4px 12px 2px", display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: "#94a3b8" }}>ЦІНА</span>
            <span style={{ fontSize: 9, color: "#94a3b8" }}>{Math.round(prices[prices.length - 1]).toLocaleString("uk-UA")} ₴</span>
          </div>
          <svg width={W} height={priceH}>
            {[priceMin + priceRange * 0.25, priceMin + priceRange * 0.75].map((tick, ti) => {
              const yy = pyPrice(tick);
              return (
                <g key={ti}>
                  <line x1={pricePAD.left} x2={W - pricePAD.right} y1={yy} y2={yy} stroke="#1e293b" strokeWidth={1} strokeDasharray="4,4" />
                  <text x={pricePAD.left - 4} y={yy + 4} textAnchor="end" fontSize={8} fill="#475569">
                    {Math.round(tick).toLocaleString("uk-UA")}
                  </text>
                </g>
              );
            })}
            {pricePts.length >= 2 && (
              <polyline points={pricePts.join(" ")} fill="none" stroke="#60a5fa" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
            )}
            {data.map((d, i) => {
              if (d.price == null) return null;
              const isLast = i === data.length - 1;
              return (
                <circle key={i} cx={pxPrice(i)} cy={pyPrice(d.price)} r={isLast ? 3.5 : 2}
                  fill={isLast ? "#60a5fa" : "#0f172a"} stroke="#60a5fa" strokeWidth={1.5} />
              );
            })}
          </svg>
        </>
      )}
    </div>,
    document.body
  );
}

function Sparkline({ data }) {
  const [hover, setHover] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const ref = useRef(null);
  const onEnter = useCallback(() => { if (ref.current) setAnchorRect(ref.current.getBoundingClientRect()); setHover(true); }, []);
  const onLeave = useCallback(() => setHover(false), []);
  if (!data || data.length < 2) return <span style={{ color: C.text4, fontSize: 11 }}>—</span>;

  const vals = data.map(d => d.pos ?? 181);
  const first = vals[0], last = vals[vals.length - 1];
  const color = last < first ? "#22c55e" : last > first ? "#f87171" : "#94a3b8";
  const W = 44, H = 14;
  const min = Math.min(...vals), max = Math.max(...vals), range = max - min || 1;
  const px = i => (i / (vals.length - 1)) * W;
  const py = v => 1 + ((v - min) / range) * (H - 2);
  const pts = vals.map((v, i) => `${px(i)},${py(v)}`).join(" ");

  return (
    <span ref={ref} onMouseEnter={onEnter} onMouseLeave={onLeave} style={{ display: "inline-block", cursor: "pointer" }}>
      <svg width={W} height={H} style={{ display: "block" }}>
        <polyline points={pts} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={px(vals.length - 1)} cy={py(last)} r={2} fill={color} />
      </svg>
      {hover && <PositionChart data={data} anchorRect={anchorRect} />}
    </span>
  );
}

function ProductDropdown({ products, value, onChange, onClose }) {
  const [search, setSearch] = useState("");
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const filtered = products.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase())
  ).slice(0, 50);

  return (
    <div style={{
      position: "absolute", zIndex: 200, top: "100%", left: 0,
      background: "#0f172a", border: `1px solid ${C.border}`,
      borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
      width: 340,
    }}>
      <div style={{ padding: "6px 8px", borderBottom: `1px solid ${C.border}` }}>
        <input ref={inputRef} value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Пошук товару..."
          style={{ ...S.input, width: "100%", fontSize: 11, padding: "4px 8px" }} />
      </div>
      <div style={{ overflowY: "auto", maxHeight: 220 }}>
        <div
          onClick={() => { onChange(null); onClose(); }}
          style={{ padding: "6px 10px", fontSize: 11, color: C.text4, cursor: "pointer" }}
          onMouseEnter={e => e.currentTarget.style.background = "#1e293b"}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
          — Не прив'язано
        </div>
        {filtered.map(p => (
          <div key={p.id}
            onClick={() => { onChange(p); onClose(); }}
            style={{ padding: "6px 10px", cursor: "pointer", borderTop: `1px solid ${C.border}22` }}
            onMouseEnter={e => e.currentTarget.style.background = "#1e293b"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <div style={{ fontSize: 11, fontWeight: 600, color: p.id === value ? C.blue : C.text1 }}>{p.name}</div>
            <div style={{ fontSize: 10, color: C.text4 }}>
              {p.catalog_price ? `${Math.round(p.catalog_price).toLocaleString("uk-UA")} ₴` : ""}
              {p.category_name ? ` · ${p.category_name}` : ""}
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div style={{ padding: "10px", fontSize: 11, color: C.text4 }}>Нічого не знайдено</div>}
      </div>
    </div>
  );
}

export default function CompetitorsPage() {
  const [competitors, setCompetitors] = useState([]);
  const [history, setHistory] = useState({});
  const [myProducts, setMyProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [urlInput, setUrlInput] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [newCatUrl, setNewCatUrl] = useState("");
  const [newLinkedProduct, setNewLinkedProduct] = useState(null); // product obj
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [editCatId, setEditCatId] = useState(null);
  const [editCatUrl, setEditCatUrl] = useState("");
  const [linkOpenId, setLinkOpenId] = useState(null); // competitor id with open product dropdown
  const [showProductDropdown, setShowProductDropdown] = useState(false); // for add form
  const addDropdownRef = useRef(null);

  const tkn = () => localStorage.getItem("crm_token");

  const load = async () => {
    setLoading(true);
    try {
      const [compRes, histRes] = await Promise.all([
        fetch(`${BASE}/api/competitors`, { headers: { Authorization: "Bearer " + tkn() } }).then(r => r.json()),
        fetch(`${BASE}/api/competitors/positions/history`, { headers: { Authorization: "Bearer " + tkn() } }).then(r => r.json()),
      ]);
      setCompetitors(compRes.competitors || []);
      setHistory(histRes.history || {});
    } finally {
      setLoading(false);
    }
  };

  const loadMyProducts = async () => {
    const res = await fetch(`${BASE}/api/competitors/products-list`, { headers: { Authorization: "Bearer " + tkn() } });
    if (res.ok) {
      const d = await res.json();
      setMyProducts(d.products || []);
    }
  };

  useEffect(() => { load(); loadMyProducts(); }, []);

  // Close add-form dropdown on outside click
  useEffect(() => {
    if (!showProductDropdown) return;
    const handler = (e) => {
      if (addDropdownRef.current && !addDropdownRef.current.contains(e.target)) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showProductDropdown]);

  const handleAdd = async () => {
    if (!urlInput.trim()) return;
    setAdding(true);
    setError("");
    try {
      const res = await fetch(`${BASE}/api/competitors`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + tkn() },
        body: JSON.stringify({ url: urlInput.trim(), name: nameInput.trim() || null }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || "Помилка"); return; }

      const newId = data.id;
      // Set category URL if provided
      if (newCatUrl.trim()) {
        await fetch(`${BASE}/api/competitors/${newId}/category-url`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: "Bearer " + tkn() },
          body: JSON.stringify({ category_url: newCatUrl.trim() }),
        });
      }
      // Link product if selected
      if (newLinkedProduct) {
        await fetch(`${BASE}/api/competitors/${newId}/link-product`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: "Bearer " + tkn() },
          body: JSON.stringify({ my_product_id: newLinkedProduct.id }),
        });
      }

      setUrlInput(""); setNameInput(""); setNewCatUrl(""); setNewLinkedProduct(null);
      load();
    } finally {
      setAdding(false);
    }
  };

  const handleSaveCatUrl = async (id) => {
    if (!editCatUrl.trim()) return;
    await fetch(`${BASE}/api/competitors/${id}/category-url`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + tkn() },
      body: JSON.stringify({ category_url: editCatUrl.trim() }),
    });
    setEditCatId(null);
    load();
  };

  const handleLinkProduct = async (compId, product) => {
    await fetch(`${BASE}/api/competitors/${compId}/link-product`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + tkn() },
      body: JSON.stringify({ my_product_id: product ? product.id : null }),
    });
    setLinkOpenId(null);
    load();
  };

  const handleDelete = async (id) => {
    setDeleteId(id);
    await fetch(`${BASE}/api/competitors/${id}`, {
      method: "DELETE",
      headers: { Authorization: "Bearer " + tkn() },
    });
    setDeleteId(null);
    load();
  };

  const fmtUAH = v => v ? Math.round(v).toLocaleString("uk-UA") + " ₴" : "—";

  const posColor = (pos, prev) => {
    if (!pos || pos >= 181) return C.text4;
    if (!prev) return C.text2;
    return pos < prev ? "#22c55e" : pos > prev ? "#f87171" : C.text2;
  };

  const posTrend = (pos, prev) => {
    if (!prev || pos === prev) return null;
    return pos < prev ? `↑${prev - pos}` : `↓${pos - prev}`;
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 800 }}>Конкуренти</div>
        <div style={{ fontSize: 12, color: C.text3 }}>{competitors.length} товарів відстежується</div>
      </div>

      {/* Add form */}
      <div style={{ ...S.card, padding: "14px 16px", marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.text2, marginBottom: 10 }}>Додати конкурента</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
          <input
            placeholder="Посилання на товар Rozetka (https://rozetka.com.ua/...)"
            value={urlInput} onChange={e => setUrlInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleAdd()}
            style={{ ...S.input, width: 380, fontSize: 12 }}
          />
          <input
            placeholder="Назва (необов'язково)"
            value={nameInput} onChange={e => setNameInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleAdd()}
            style={{ ...S.input, width: 180, fontSize: 12 }}
          />
          {/* Own product picker */}
          <div ref={addDropdownRef} style={{ position: "relative" }}>
            <button onClick={() => setShowProductDropdown(v => !v)}
              style={{ ...S.input, fontSize: 11, cursor: "pointer", textAlign: "left", width: 200, padding: "7px 10px",
                color: newLinkedProduct ? C.text1 : C.text4,
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {newLinkedProduct ? newLinkedProduct.name : "Мій товар (необов'язково)"}
              </span>
              <span style={{ color: C.text4, fontSize: 10 }}>▾</span>
            </button>
            {showProductDropdown && (
              <ProductDropdown
                products={myProducts}
                value={newLinkedProduct?.id}
                onChange={p => {
                  setNewLinkedProduct(p);
                  if (p && p.category_url && !newCatUrl) setNewCatUrl(p.category_url);
                }}
                onClose={() => setShowProductDropdown(false)}
              />
            )}
          </div>
          <button onClick={handleAdd} disabled={adding || !urlInput.trim()} style={{
            padding: "7px 20px", borderRadius: 9, fontSize: 12, fontWeight: 700,
            background: `linear-gradient(135deg, ${C.blue}, #3b82f6)`,
            color: "#fff", border: "none", cursor: adding ? "default" : "pointer",
            opacity: adding ? 0.6 : 1,
          }}>
            {adding ? "Додаємо..." : "+ Додати"}
          </button>
        </div>

        {/* Category URL row */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
          <span style={{ fontSize: 11, color: C.text4, whiteSpace: "nowrap" }}>URL категорії:</span>
          <input
            placeholder="https://rozetka.com.ua/ua/.../cXXXXXX/  (підказується з вашого товару)"
            value={newCatUrl} onChange={e => setNewCatUrl(e.target.value)}
            style={{ ...S.input, flex: 1, fontSize: 11 }}
          />
          {newLinkedProduct && newLinkedProduct.category_url && newCatUrl !== newLinkedProduct.category_url && (
            <button onClick={() => setNewCatUrl(newLinkedProduct.category_url)}
              style={{ fontSize: 10, color: C.blue, background: "none", border: "none", cursor: "pointer", whiteSpace: "nowrap" }}>
              ↺ з товару
            </button>
          )}
        </div>

        {error && <div style={{ fontSize: 12, color: C.pink, marginTop: 8 }}>{error}</div>}
        <div style={{ fontSize: 11, color: C.text4, marginTop: 8 }}>
          Після додавання запустіть <code style={{ color: C.blue }}>python3 scraper/competitor_sync.py</code> щоб спарсити позиції
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ color: C.text4, padding: 20 }}>Завантаження...</div>
      ) : competitors.length === 0 ? (
        <div style={{ ...S.card, padding: 32, textAlign: "center", color: C.text4 }}>
          Конкурентів ще немає. Додайте перший товар вище.
        </div>
      ) : (
        <div style={{ ...S.card, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Товар конкурента", "Мій товар", "Ціна", "Позиція", "Динаміка", "Оновлено", ""].map(h => (
                  <th key={h} style={{ ...S.th, textAlign: "left", padding: "10px 14px" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {competitors.map(c => {
                const hist = history[String(c.id)];
                const trend = posTrend(c.position, c.position_prev);
                const col = posColor(c.position, c.position_prev);
                const ownProduct = c.my_product_id ? myProducts.find(p => p.id === c.my_product_id) : null;
                const competitorCheaper = ownProduct && c.price && ownProduct.catalog_price &&
                  c.price < ownProduct.catalog_price * 0.99;

                return (
                  <tr key={c.id} style={{ borderTop: `1px solid ${C.border}` }}>

                    {/* Competitor product */}
                    <td style={{ ...S.td, padding: "12px 14px", maxWidth: 320 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {c.image_url ? (
                          <img src={c.image_url} alt="" style={{ width: 40, height: 40, objectFit: "contain", borderRadius: 8, background: "#1e293b", flexShrink: 0 }} />
                        ) : (
                          <div style={{ width: 40, height: 40, borderRadius: 8, background: "#1e293b", flexShrink: 0 }} />
                        )}
                        <div style={{ minWidth: 0 }}>
                          <a href={c.url} target="_blank" rel="noreferrer"
                            style={{ fontSize: 13, fontWeight: 600, color: C.text1, textDecoration: "none", display: "block",
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 240 }}
                            onMouseEnter={e => e.currentTarget.style.color = C.blue}
                            onMouseLeave={e => e.currentTarget.style.color = C.text1}>
                            {c.name || `Товар #${c.rozetka_id}`}
                          </a>
                          {editCatId === c.id ? (
                            <div style={{ display: "flex", gap: 4, marginTop: 6, alignItems: "center" }}>
                              <input autoFocus value={editCatUrl}
                                onChange={e => setEditCatUrl(e.target.value)}
                                onKeyDown={e => { if (e.key === "Enter") handleSaveCatUrl(c.id); if (e.key === "Escape") setEditCatId(null); }}
                                placeholder="https://rozetka.com.ua/ua/.../cXXXXXX/..."
                                style={{ ...S.input, fontSize: 11, width: 260, padding: "3px 8px" }}
                              />
                              <button onClick={() => handleSaveCatUrl(c.id)}
                                style={{ background: "#16a34a", border: "none", borderRadius: 6, color: "#fff", fontSize: 11, fontWeight: 700, padding: "4px 10px", cursor: "pointer" }}>
                                Зберегти
                              </button>
                              <button onClick={() => setEditCatId(null)}
                                style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, color: C.text4, fontSize: 11, padding: "4px 8px", cursor: "pointer" }}>
                                Скасувати
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                              {c.category_url ? (
                                <span style={{ fontSize: 10, color: "#22c55e", fontWeight: 600, letterSpacing: "0.02em" }}>
                                  ✓ категорія задана
                                </span>
                              ) : (
                                <span style={{ fontSize: 10, color: C.yellow, fontWeight: 600 }}>
                                  ⚠ категорія не задана
                                </span>
                              )}
                              <button onClick={() => { setEditCatId(c.id); setEditCatUrl(c.category_url || ""); }}
                                style={{ fontSize: 10, color: C.text4, background: "none", border: `1px solid ${C.border}`, borderRadius: 4,
                                  cursor: "pointer", padding: "1px 6px", lineHeight: "16px" }}
                                onMouseEnter={e => { e.currentTarget.style.color = C.blue; e.currentTarget.style.borderColor = C.blue; }}
                                onMouseLeave={e => { e.currentTarget.style.color = C.text4; e.currentTarget.style.borderColor = C.border; }}>
                                змінити
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Own product */}
                    <td style={{ ...S.td, padding: "12px 14px", maxWidth: 220 }}>
                      <div style={{ position: "relative" }}>
                        {ownProduct ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: C.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 160 }}
                                title={ownProduct.name}>{ownProduct.name}</div>
                              <div style={{ fontSize: 11, color: C.text3, marginTop: 1, display: "flex", gap: 8 }}>
                                {ownProduct.catalog_price ? <span>{fmtUAH(ownProduct.catalog_price)}</span> : null}
                                {ownProduct.position ? (
                                  <span style={{ color: C.text4 }}>поз. #{ownProduct.position}</span>
                                ) : null}
                              </div>
                            </div>
                            <button onClick={() => setLinkOpenId(linkOpenId === c.id ? null : c.id)}
                              style={{ flexShrink: 0, fontSize: 10, color: C.text4, background: "none", border: `1px solid ${C.border}`,
                                borderRadius: 4, cursor: "pointer", padding: "1px 6px", lineHeight: "16px" }}
                              onMouseEnter={e => { e.currentTarget.style.color = C.blue; e.currentTarget.style.borderColor = C.blue; }}
                              onMouseLeave={e => { e.currentTarget.style.color = C.text4; e.currentTarget.style.borderColor = C.border; }}>
                              змінити
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => setLinkOpenId(linkOpenId === c.id ? null : c.id)}
                            style={{ fontSize: 11, color: C.blue, background: "none", border: `1px solid ${C.blue}44`,
                              borderRadius: 6, cursor: "pointer", padding: "4px 10px", fontWeight: 600 }}
                            onMouseEnter={e => e.currentTarget.style.background = `${C.blue}11`}
                            onMouseLeave={e => e.currentTarget.style.background = "none"}>
                            + прив'язати товар
                          </button>
                        )}
                        {linkOpenId === c.id && (
                          <ProductDropdown
                            products={myProducts}
                            value={c.my_product_id}
                            onChange={p => handleLinkProduct(c.id, p)}
                            onClose={() => setLinkOpenId(null)}
                          />
                        )}
                      </div>
                    </td>

                    {/* Price */}
                    <td style={{ ...S.td, padding: "12px 14px", background: competitorCheaper ? "rgba(248,113,113,0.07)" : "transparent" }}>
                      {c.price ? (
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: competitorCheaper ? "#f87171" : C.text1 }}>
                            {fmtUAH(c.price)}
                          </div>
                          {c.price_prev && c.price !== c.price_prev && (
                            <div style={{ fontSize: 10, color: c.price < c.price_prev ? "#22c55e" : "#f87171", marginTop: 1 }}>
                              {c.price < c.price_prev ? "↓ " : "↑ "}{fmtUAH(Math.abs(c.price - c.price_prev))}
                            </div>
                          )}
                          {competitorCheaper && ownProduct && (
                            <div style={{ fontSize: 10, color: C.text4, marginTop: 1 }}>
                              мій: {fmtUAH(ownProduct.catalog_price)}
                            </div>
                          )}
                        </div>
                      ) : <span style={{ color: C.text4, fontSize: 12 }}>—</span>}
                    </td>

                    {/* Position + trend */}
                    <td style={{ ...S.td, padding: "12px 14px" }}>
                      {c.position ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 15, fontWeight: 800, color: col }}>
                            #{c.position >= 181 ? ">180" : c.position}
                          </span>
                          {trend && (
                            <span style={{ fontSize: 11, fontWeight: 700, color: col,
                              background: `${col}18`, borderRadius: 4, padding: "1px 5px" }}>
                              {trend}
                            </span>
                          )}
                        </div>
                      ) : <span style={{ color: C.text4, fontSize: 12 }}>—</span>}
                    </td>

                    {/* Sparkline */}
                    <td style={{ ...S.td, padding: "12px 14px" }}>
                      <Sparkline data={hist} />
                    </td>

                    {/* Updated */}
                    <td style={{ ...S.td, padding: "12px 14px", color: C.text4, fontSize: 11, whiteSpace: "nowrap" }}>
                      {c.position_synced_at || "—"}
                    </td>

                    {/* Delete */}
                    <td style={{ ...S.td, padding: "12px 14px" }}>
                      <button onClick={() => handleDelete(c.id)} disabled={deleteId === c.id}
                        style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6,
                          color: C.text4, cursor: "pointer", padding: "4px 8px", fontSize: 11 }}
                        onMouseEnter={e => { e.currentTarget.style.color = C.pink; e.currentTarget.style.borderColor = C.pink; }}
                        onMouseLeave={e => { e.currentTarget.style.color = C.text4; e.currentTarget.style.borderColor = C.border; }}>
                        {deleteId === c.id ? "..." : "✕"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
